import Head from 'next/head';
import PropTypes from 'prop-types';
import SEO from '@seo/index';
import PlusCourses from '@cont/PlusCourses/PlusCourses';
import {
  fetchTopologyInfo
  // fetchTopologyNotes
} from '@actions/topologies';
import {
  fetchTopologyEnrolledCourses,
  fetchRecommendedCourses,
  fetchCompletedCourses,
  fetchMoreCoursesFilters,
  fetchMoreCourses
} from '@actions/courses';
import { useSetSelectedNav } from '@stores/sidenav';
import subTopicSchema from '@seo/topic/topic';
import {
  getIsSyllabusV1Enabled,
  getIsTopologySelfDataFetched,
  getTopologyDataByUID
} from '@selectors/topologies';
import { getIsLoggedIn } from '@selectors/auth';
import {
  hasCompletedCourses,
  hasEnrolledCourses,
  hasRecommendedCourses
} from '@selectors/syllabus';
import { getNoFiltersKey } from '@cont/PlusCourses/syllabusHelper';
import { TOPIC_COURSES_SYLLABUS_PAGE } from '@constants/segment';
import setupInitialProps from '@utils/setupInitialProps';
import { redirectToRoute } from '@utils/route';
import {
  getIsSubscribedToPlus,
  getIsSubscribedToOffline
} from '@selectors/subscription';
import redirect from '@cont/App/redirect';
import isEmptyObject from '@utils/isEmptyObject';
import Layout from '@layout/index';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

const PlusTopicPage = ({
  goalInfo,
  topicInfo,
  showRecommended,
  isContentAvailable
}) => {
  const {
    name: goalName,
    goalSlug,
    uid: goalUID,
    thumbnail: goalThumbnail
  } = goalInfo || {};
  const {
    uid: topicUID,
    name: topicName,
    slug: topicSlug,
    thumbnail: topicThumbnail,
    parent = {}
  } = topicInfo || {};
  const metaTitle = `${goalName} ${topicName} - Prepare with Unacademy`;
  const metaDescription = `Enroll for ${goalName} ${topicName} Course on Unacademy and learn
  from Top Educators. Subscribe today and get access to complete syllabus and course.`;
  const canonicalURL = `https://unacademy.com/goal/${goalSlug}/${goalUID}/topic/${topicSlug}/${topicUID}`;
  useSetSelectedNav('topics');
  return (
    <>
      <SEO
        title={metaTitle}
        description={metaDescription}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          images: [
            {
              url: topicThumbnail || goalThumbnail
            }
          ],
          url: canonicalURL
        }}
        schema={subTopicSchema({
          goal: goalInfo,
          topic: {
            name: (parent || {}).name,
            permalink: `https://unacademy.com/goal/${goalSlug}/${goalUID}/topic/${topicSlug}/`
          },
          others: [{ name: topicName, permalink: canonicalURL }],
          type: 'courses',
          topicInfo,
          description: metaDescription
        })}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      <PlusCourses
        goalUID={goalUID}
        topicUid={topicUID}
        showRecommended={showRecommended}
        isContentAvailable={isContentAvailable}
      />
    </>
  );
};

PlusTopicPage.pageInfo = {
  isMWebSupported: true,
  name: TOPIC_COURSES_SYLLABUS_PAGE,
  layoutOptions: {
    showPlusNav: true
  }
};

export default PlusTopicPage;

PlusTopicPage.getInitialProps = setupInitialProps(
  async ({ store, query, isServer, res }) => {
    const { goalUID, topicUid, goalSlug } = query;
    let showRecommended = false;
    if (goalUID === 'undefined' || topicUid === 'undefined')
      // Clearing out incorrect URLs from sitemap
      return redirect(
        isServer,
        { as: '/404', href: '/404' },
        { res },
        {},
        true
      );

    if (goalUID && topicUid) {
      const promises = [];
      let state = store.getState();
      if (!getIsTopologySelfDataFetched(state, goalUID))
        promises.push(store.dispatch(fetchTopologyInfo(goalUID)));
      if (!getIsTopologySelfDataFetched(state, topicUid))
        promises.push(store.dispatch(fetchTopologyInfo(topicUid)));
      if (
        getIsLoggedIn(state) &&
        (getIsSubscribedToPlus(goalUID)(state) ||
          getIsSubscribedToOffline(goalUID)(state))
      ) {
        await store.dispatch(fetchTopologyEnrolledCourses(topicUid));
        state = store.getState();
        if (!hasEnrolledCourses(state, topicUid)) {
          promises.push(store.dispatch(fetchRecommendedCourses(topicUid)));
          showRecommended = true;
        }
        promises.push(store.dispatch(fetchCompletedCourses(topicUid)));
      } else {
        promises.push(store.dispatch(fetchRecommendedCourses(topicUid)));
        showRecommended = true;
      }
      promises.push(store.dispatch(fetchMoreCoursesFilters(goalUID)));
      promises.push(
        store.dispatch(
          fetchMoreCourses({ topicUid, key: getNoFiltersKey(topicUid) })
        )
      );
      // promises.push(store.dispatch(fetchTopologyNotes(topicUid)));

      await Promise.all(promises);
    }
    const state = store.getState();
    const topologyInfo = getTopologyDataByUID(goalUID)(state);
    if (isEmptyObject(topologyInfo))
      return redirect(isServer, { as: '/404', href: '/404' }, { res });

    const topicGroup = state.topologies?.data[topicUid]?.parent;
    const { uid: topicGroupUID } = topicGroup || {};
    const isNewSyllabusEnabled = getIsSyllabusV1Enabled(state, goalUID);

    if (isNewSyllabusEnabled && topicGroupUID) {
      redirectToRoute(
        isServer,
        res,
        `/goal/${goalSlug}/${goalUID}/topic/${topicGroupUID}/${topicUid}`
      );
    }

    const isContentAvailable =
      hasEnrolledCourses(state, topicUid) ||
      hasRecommendedCourses(state, topicUid) ||
      hasCompletedCourses(state, topicUid);
    return {
      goalInfo: state.topologies.data[goalUID],
      topicInfo: state.topologies.data[topicUid],
      showRecommended,
      isContentAvailable
    };
  },
  PlusTopicPage.pageInfo
);
PlusTopicPage.propTypes = {
  goalInfo: PropTypes.objectOf(PropTypes.any).isRequired,
  topicInfo: PropTypes.objectOf(PropTypes.any).isRequired,
  showRecommended: PropTypes.bool.isRequired,
  isContentAvailable: PropTypes.bool.isRequired
};
PlusTopicPage.Layout = Layout;
PlusTopicPage.Wrapper = PageReduxProvider;
